import { computed, effect, Injectable, signal } from '@angular/core';

/** Three-mode preference. {@code 'system'} follows the OS-level
 * {@code prefers-color-scheme}; the other two are explicit overrides. */
export type ThemeMode = 'system' | 'light' | 'dark';

/** Resolved palette — what we actually paint with. {@code 'system'} folds into
 * one of these via {@code matchMedia}. */
export type ResolvedTheme = 'light' | 'dark';

/** New (shared) storage key. */
const STORAGE_KEY = 'wheere.themeMode';

/** Legacy keys that were per-app before this service moved to shared-ui. We
 * read them once on bootstrap so users don't lose their preference, then
 * rewrite under the new shared key. */
const LEGACY_STORAGE_KEYS = ['wheere:integrator:themeMode'];

/**
 * Shared Wheere theme service. Owns the user preference (signal-backed,
 * persisted to {@code localStorage}) plus the resolved {@code 'light'} /
 * {@code 'dark'} class applied on {@code <html>}.
 *
 * <p>Three modes:
 * <ul>
 *   <li><b>system</b> (default) — follows {@code prefers-color-scheme}. We
 *       subscribe to the {@code matchMedia} change event so flipping the OS
 *       theme at runtime updates the page without a refresh.</li>
 *   <li><b>light</b> — explicit. Class {@code dark} removed, OS preference
 *       ignored.</li>
 *   <li><b>dark</b> — explicit. Class {@code dark} added, OS preference
 *       ignored.</li>
 * </ul>
 *
 * <p>SSR-safe: every {@code window} / {@code document} access is guarded so
 * the service can be instantiated during a Node prerender pass without
 * throwing.
 */
@Injectable({ providedIn: 'root' })
export class ThemeService {
  /** User preference. Hydrated from localStorage on construct, falls back to
   * 'system' for fresh visitors. */
  readonly mode = signal<ThemeMode>(this.#initialMode());

  /** Tracks the OS preference. Re-emitted by the matchMedia listener so the
   * resolved theme stays in sync when the OS toggles. */
  readonly #systemPrefersDark = signal<boolean>(this.#initialSystemPrefersDark());

  /** Final palette that drives the {@code dark} class. */
  readonly resolved = computed<ResolvedTheme>(() => {
    const m = this.mode();
    if (m === 'dark') return 'dark';
    if (m === 'light') return 'light';
    return this.#systemPrefersDark() ? 'dark' : 'light';
  });

  constructor() {
    // Apply the resolved theme as a class on <html> every time it changes.
    // We toggle <html> (not <body>) so PrimeNG / CDK overlays which append
    // to <body> still inherit the theme via specificity.
    effect(() => {
      const theme = this.resolved();
      if (typeof document !== 'undefined') {
        document.documentElement.classList.toggle('dark', theme === 'dark');
        // Also expose the resolved theme as a data attr so external CSS
        // (PrimeNG theme overrides, scrollbar tweaks) can hook in without
        // having to read the class.
        document.documentElement.dataset['theme'] = theme;
      }
    });

    // Persist the user preference when it changes — but only if it's an
    // explicit override. A 'system' setting writes the literal 'system' so we
    // distinguish "user picked auto" from "user never visited".
    effect(() => {
      const m = this.mode();
      if (typeof window !== 'undefined') {
        try {
          window.localStorage.setItem(STORAGE_KEY, m);
        } catch {
          // localStorage unavailable (Safari private mode, quota) — best-effort.
        }
      }
    });

    // Watch OS preference changes. addEventListener('change') is the modern API;
    // older Safari (< 14) needs addListener but we don't target that.
    if (typeof window !== 'undefined' && typeof window.matchMedia === 'function') {
      const mql = window.matchMedia('(prefers-color-scheme: dark)');
      mql.addEventListener('change', e => this.#systemPrefersDark.set(e.matches));
    }
  }

  /** Pick a specific mode. */
  setMode(mode: ThemeMode): void {
    this.mode.set(mode);
  }

  /** Cycle through the three modes — used by the navbar toggle. Order matches
   * what most apps do: system → light → dark → system. */
  cycle(): void {
    this.mode.update(curr => (curr === 'system' ? 'light' : curr === 'light' ? 'dark' : 'system'));
  }

  // ── Initialisation helpers (kept private + non-reactive)

  #initialMode(): ThemeMode {
    if (typeof window === 'undefined') return 'system';
    try {
      const fresh = window.localStorage.getItem(STORAGE_KEY);
      if (fresh === 'light' || fresh === 'dark' || fresh === 'system') return fresh;
      // Legacy single-app keys — read once, migrate to the shared key.
      for (const legacy of LEGACY_STORAGE_KEYS) {
        const stored = window.localStorage.getItem(legacy);
        if (stored === 'light' || stored === 'dark' || stored === 'system') {
          try {
            window.localStorage.setItem(STORAGE_KEY, stored);
            window.localStorage.removeItem(legacy);
          } catch {
            // best-effort migration
          }
          return stored;
        }
      }
    } catch {
      // ignore
    }
    return 'system';
  }

  #initialSystemPrefersDark(): boolean {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  }
}
